import { useState } from "react";
import { toast } from "sonner";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import { api, formatApiError } from "../lib/api";
import { useConfig } from "../lib/config";
import { Phone, Mail, MessageCircle, MapPin, Send } from "lucide-react";

const ADDRESS_LINE = "Fulya Mah. Garaj Sk. No:7 Kat:1 Daire:5, Şişli / İstanbul";
const MAPS_QUERY = encodeURIComponent("Fulya Mah Garaj Sokak No 7 Şişli İstanbul");

export default function Contact() {
  const cfg = useConfig();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Mesajınız iletildi. En kısa sürede dönüş yapacağız.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Mesaj gönderilemedi.");
    } finally {
      setLoading(false);
    }
  };

  const phone = cfg.phone || "+90 505 745 35 32";
  const phoneDigits = phone.replace(/\D/g, "");
  const waLink = `https://wa.me/${(cfg.whatsapp || phone).replace(/\D/g, "")}`;
  const email = cfg.email || "info@fardriverturkiye.com";

  return (
    <div data-testid="contact-page">
      <SEO
        title="İletişim"
        description="Fardriver Türkiye iletişim: 0505 745 35 32, Fulya Mah Garaj Sk No:7 Şişli/İstanbul. Elektrikli motor kontrolör teklif ve danışmanlık."
        path="/iletisim"
      />
      <PageHero
        overline="İletişim"
        title="Bizimle iletişime geçin."
        description="Soru, teklif veya teknik destek talepleriniz için bize ulaşın. Genellikle 24 saat içinde dönüş yapıyoruz."
      />

      <section className="max-w-7xl mx-auto px-6 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <form onSubmit={submit} data-testid="contact-form" className="border border-gray-200 bg-white p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] text-gray-600 mb-2">Ad Soyad *</label>
                <input
                  required
                  data-testid="contact-name"
                  value={form.name}
                  onChange={update("name")}
                  className="w-full bg-gray-50 border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#059669] focus:ring-1 focus:ring-[#059669]"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] text-gray-600 mb-2">E-posta *</label>
                <input
                  required
                  type="email"
                  data-testid="contact-email-input"
                  value={form.email}
                  onChange={update("email")}
                  className="w-full bg-gray-50 border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#059669] focus:ring-1 focus:ring-[#059669]"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] text-gray-600 mb-2">Telefon</label>
                <input
                  data-testid="contact-phone-input"
                  value={form.phone}
                  onChange={update("phone")}
                  className="w-full bg-gray-50 border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#059669] focus:ring-1 focus:ring-[#059669]"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] text-gray-600 mb-2">Konu</label>
                <input
                  data-testid="contact-subject"
                  value={form.subject}
                  onChange={update("subject")}
                  className="w-full bg-gray-50 border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#059669] focus:ring-1 focus:ring-[#059669]"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs uppercase tracking-[0.18em] text-gray-600 mb-2">Mesajınız *</label>
                <textarea
                  required
                  rows={6}
                  data-testid="contact-message"
                  value={form.message}
                  onChange={update("message")}
                  className="w-full bg-gray-50 border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#059669] focus:ring-1 focus:ring-[#059669] resize-none"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="mt-6 inline-flex items-center gap-2 bg-[#059669] text-white px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.15em] hover:bg-emerald-700 disabled:opacity-50"
            >
              {loading ? "Gönderiliyor..." : <>Mesaj Gönder <Send size={14} /></>}
            </button>
          </form>
        </div>

        <div className="lg:col-span-5">
          <div className="border border-gray-200 bg-white p-8 space-y-6">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#059669] mb-2">Hızlı İletişim</p>
              <h3 className="font-display text-2xl tracking-tighter font-black">Direkt bize ulaşın</h3>
            </div>
            <div className="space-y-4">
              <a href={`tel:+${phoneDigits}`} data-testid="quick-phone" className="flex items-start gap-4 group">
                <Phone size={20} className="text-[#059669] mt-1" />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-gray-500">Telefon</p>
                  <p className="font-display text-lg group-hover:text-[#059669]">0505 745 35 32</p>
                </div>
              </a>
              <a href={`mailto:${email}`} data-testid="quick-email" className="flex items-start gap-4 group">
                <Mail size={20} className="text-[#059669] mt-1" />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-gray-500">E-posta</p>
                  <p className="font-display text-lg group-hover:text-[#059669] break-all">{email}</p>
                </div>
              </a>
              <a href={waLink} target="_blank" rel="noreferrer" data-testid="quick-whatsapp" className="flex items-start gap-4 group">
                <MessageCircle size={20} className="text-emerald-500 mt-1" />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-gray-500">WhatsApp</p>
                  <p className="font-display text-lg group-hover:text-emerald-600">Anında mesajlaş</p>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <MapPin size={20} className="text-[#059669] mt-1" />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-gray-500">Adres</p>
                  <p className="text-sm leading-relaxed">{ADDRESS_LINE}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps */}
      <section className="border-t border-gray-200" data-testid="contact-map">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#059669] mb-4">Konum</p>
          <h2 className="font-display text-2xl md:text-3xl tracking-tighter font-black mb-6">Şişli / İstanbul</h2>
          <div className="border border-gray-200 overflow-hidden bg-gray-100">
            <iframe
              title="Fardriver Türkiye - Şişli İstanbul"
              src={`https://maps.google.com/maps?q=${MAPS_QUERY}&t=&z=16&ie=UTF8&iwloc=&output=embed`}
              width="100%"
              height="450"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${MAPS_QUERY}`}
            target="_blank"
            rel="noreferrer"
            className="inline-block mt-4 text-sm text-[#059669] font-semibold underline"
          >
            Google Haritalar'da aç →
          </a>
        </div>
      </section>
    </div>
  );
}
